import React, { useState, useEffect, forwardRef } from 'react';
import { connectionService, datasourceTypeService, systemService } from '../services';
import {
  Checkbox,
  CustomPropertiesForm,
  Select,
  SelectOwner,
  InputStatus,
  SelectButtons,
  FormTabs,
  formatSelectOptions,
  Input,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { getBatchEditValue, Asset } from '../helpers';
import { useCustomProperties, useDescriptionSource, useGlobalState, useMessage } from '../hooks';

const _AddDatasourceForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    datasource_id: null,
    datasource_name: '',
    datasource_hostname: '',
    datasource_port: '',
    datasource_database: '',
    datasource_description: textareaInitialState({}),
    datasource_source_description: textareaInitialState({}),
    datasource_source_description_secondary: textareaInitialState({}),
    datasource_description_source: 'k',
    datasource_type_id: '',
    datasource_type_other_name: '',
    datasource_service: '',
    system_id: '',
    status_id: '',
    owner_principal_id: '',
  };

  const initialBatchEditItems = {
    batch_datasource_type_id: false,
    batch_system_id: false,
    batch_status_id: false,
    batch_owner_principal_id: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentSystem] = useGlobalState('currentSystem');
  const [statuses] = useGlobalState('statuses');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [datasourceTypes, setDatasourceTypes] = useState([]);
  const [datasourceType, setDatasourceType] = useState({});
  const [systems, setSystems] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const { showMessage, hideMessage } = useMessage();
  const { getDescription, getDescriptionSourceOptions } = useDescriptionSource({
    data,
    formData,
    objectName: 'datasource',
  });
  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.Datasource,
    objectIds: data.map(x => x.datasource_id),
  });

  useEffect(() => {
    // Get form data
    fetchDatasourceTypes();
    fetchSystems();
    fetchFormValues();
  }, []);

  useEffect(() => {
    setBatchEdit(false);
    if (data.length === 1) {
      setFormData(prev => {
        return {
          ...prev,
          datasource_id: mode !== 'add' ? data[0].datasource_id : null,
          datasource_name: data[0].datasource_name || '',
          datasource_hostname: data[0].datasource_hostname || '',
          datasource_port: data[0].datasource_port || '',
          datasource_database: data[0].datasource_database || '',
          datasource_description: textareaInitialState({ value: data[0].datasource_description }),
          datasource_source_description: textareaInitialState({
            value: data[0].datasource_source_description,
          }),
          datasource_source_description_secondary: textareaInitialState({
            value: data[0].datasource_source_description_secondary || '',
          }),
          datasource_description_source:
            data[0].datasource_description_source || prev.datasource_description_source,
          datasource_type_id: data[0].datasource_type_id,
          datasource_type_other_name: data[0].datasource_type_other_name || '',
          datasource_service: data[0].datasource_service || '',
          system_id: data[0].system_id,
          status_id: data[0].status_id,
          owner_principal_id: data[0].owner_principal_id,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        datasource_type_id: getBatchEditValue(data, 'datasource_type_id'),
        system_id: getBatchEditValue(data, 'system_id'),
        status_id: getBatchEditValue(data, 'status_id'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
        datasource_description_source: getBatchEditValue(data, 'datasource_description_source'),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        system_id: currentSystem.system_id,
      });
    } else {
      resetForm();
    }

    if (data.length > 0) {
      setDatasourceType(
        datasourceTypes.find(x => x.datasource_type_id === data[0].datasource_type_id)
      );
    }
  }, [mode, data, loggedInUser, currentSystem]);

  const fetchDatasourceTypes = async () => {
    datasourceTypeService
      .getAll()
      .then(res => {
        setDatasourceTypes(res.datasource_types);
        data[0] &&
          setDatasourceType(
            res.datasource_types.find(x => x.datasource_type_id === data[0].datasource_type_id)
          );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchSystems = async () => {
    systemService
      .getAll()
      .then(res => {
        setSystems(res.systems);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchFormValues = async () => {
    connectionService
      .getFormValues()
      .then(res => {
        if (res) {
          const result = res.formValues;
          setFormValues(result);

          // Set default values
          let tmp = {};
          Object.entries(formData).forEach(([key]) => {
            const ds = result[datasourceType.datasource_type_code];
            const prop = ds && (ds[key] || {});
            const defaultValue = prop && prop?.defaultValue;
            const disabled = result[datasourceType.datasource_type_code]?.[key]?.disabled;

            if (defaultValue && ((data.length < 1 && mode === 'add') || disabled)) {
              tmp[key] = defaultValue;
            }
          });
          setFormData(prev => {
            return { ...prev, ...tmp };
          });
        }
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };
  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    if (name === 'datasource_type_id') {
      setDatasourceType(datasourceTypes.find(x => x.datasource_type_id === value));
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const handleClickOnDescription = () => {
    if (formData.datasource_description_source !== 'k') {
      setFormData(prevFormData => {
        return {
          ...prevFormData,
          datasource_description_source: 'k',
        };
      });
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          datasource_id: item.datasource_id,
          datasource_name: item.datasource_name,
          datasource_hostname: item.datasource_hostname,
          datasource_port: item.datasource_port,
          datasource_database: item.datasource_database,
          datasource_type_id: item.datasource_type_id,
          datasource_service: item.datasource_service,
          system_id: item.system_id,
          status_id: item.status_id,
          owner_principal_id: item.owner_principal_id,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.datasource_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        ...getCustomPropertyValuesForSubmit(),
      };

      if (dataToSubmit.datasource_description_source !== 'k') {
        delete dataToSubmit.datasource_description;
      } else {
        dataToSubmit.datasource_description = textareaToRaw({
          value: formData.datasource_description,
        });
      }

      delete dataToSubmit.datasource_source_description;
      delete dataToSubmit.datasource_source_description_secondary;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setDatasourceType({});
    resetFormCustomPropertyValues();
    setFormData(() => {
      return {
        ...initialFormState,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
        system_id: currentSystem ? currentSystem.system_id : null,
      };
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="datasource_name"
            value={formData.datasource_name}
            statusValue={formData.status_id}
            assetCategory="system"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.datasource_name
                ? { valid: false, message: formErrors.datasource_name }
                : undefined
            }
          />
        </div>

        <div className="form-block vertical">
          {batchEdit && (
            <label>
              <Checkbox
                checked={batchEditItems.batch_datasource_type_id}
                name="batch_datasource_type_id"
                label="Edit Datasource Type"
                onChange={e => handleInputChange(e, 'batch')}
                disabled={loading}
              />
            </label>
          )}
          <Select
            name="datasource_type_id"
            label={!batchEdit}
            value={
              formData.datasource_type_id === 'multiple' ? undefined : formData.datasource_type_id
            }
            options={formatSelectOptions({
              options: datasourceTypes,
              optionValue: 'datasource_type_id',
              optionLabel: 'datasource_type_name',
              optionIcon: 'datasource_type_category',
              optionDisabled: 'datasource_type_is_disabled',
            })}
            onChange={handleInputChange}
            placeholder={
              formData.datasource_type_id === 'multiple'
                ? '< Multiple >'
                : 'Select datasource type...'
            }
            disabled={loading || (batchEdit && !batchEditItems.batch_datasource_type_id)}
            validation={
              formErrors.datasource_type_id
                ? { valid: false, message: formErrors.datasource_type_id }
                : undefined
            }
          />
        </div>

        {datasourceType && datasourceType.datasource_type_code === 'other' && (
          <div className="form-block vertical">
            <Input
              type="text"
              name="datasource_type_other_name"
              label="Datasource Type Name"
              value={formData.datasource_type_other_name}
              onChange={handleInputChange}
              autoComplete="off"
              disabled={loading}
              validation={
                formErrors.datasource_type_other_name
                  ? { valid: false, message: formErrors.datasource_type_other_name }
                  : undefined
              }
            />
          </div>
        )}

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                {
                  <Textarea
                    name="datasource_description"
                    value={getDescription()}
                    onChange={handleInputChange}
                    onClick={handleClickOnDescription}
                    disabled={loading || formData.datasource_description_source !== 'k'}
                    title={
                      formData.datasource_description_source !== 'k'
                        ? `The description is automatically synced from the datasource`
                        : undefined
                    }
                    isOptional={true}
                  />
                }
              </div>
            )}

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_datasource_description_source}
                    name="batch_datasource_description_source"
                    label="Edit Description Source"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectButtons
                name="datasource_description_source"
                value={
                  formData.datasource_description_source === 'multiple'
                    ? undefined
                    : formData.datasource_description_source
                }
                options={getDescriptionSourceOptions()}
                onChange={handleInputChange}
                disabled={
                  loading || (batchEdit && !batchEditItems.batch_datasource_description_source)
                }
                title="Select whether to use a custom description set here in Katalogue or automatically sync the description from the datasource."
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_system_id}
                    name="batch_system_id"
                    label="Edit System"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="system_id"
                label={!batchEdit}
                value={formData.system_id === 'multiple' ? undefined : formData.system_id}
                options={formatSelectOptions({
                  options: systems,
                  optionValue: 'system_id',
                  optionLabel: 'system_name',
                  optionSecondaryLabel: 'system_code',
                  tooltip: 'system_description',
                })}
                onChange={handleInputChange}
                placeholder={formData.system_id === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_system_id)}
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {!batchEdit &&
            datasourceType &&
            formValues[
              datasourceType.datasource_type_code === 'other'
                ? 'odbc'
                : datasourceType.datasource_type_code
            ] && (
              <div label="connection info">
                {Object.entries(
                  formValues[
                    datasourceType.datasource_type_code === 'other'
                      ? 'odbc'
                      : datasourceType.datasource_type_code
                  ]
                ).map(([key, field]) => {
                  const allowedFormValues = ['hostname', 'port', 'database', 'service'];

                  if (field.show && field.type && allowedFormValues.includes(key)) {
                    let element = <span></span>;

                    let fieldName = field.dbFieldName.replace('connection', 'datasource'); // Since this comes from the connection settings, the name will be 'connection_hostname', which must be converted to 'datasource-hostname' to work in this context
                    let label = field.label;
                    let tooltip =
                      field?.tooltip && field.tooltip !== '' ? field.tooltip : undefined;
                    let placeholder = field.placeholder;
                    let disabled = field.disabled || false;
                    let accept = field.acceptFileTypes;
                    let value = formData[fieldName];

                    if (field.states) {
                      Object.entries(field.states).forEach(([key, state]) => {
                        const formDataValue = formData[key] && formData[key].toString();
                        const formDataState =
                          (formDataValue && state.not_null) || state[formDataValue];

                        value = formData[formDataState?.value] || value;
                        placeholder = formDataState?.placeholder || placeholder;
                        disabled = formDataState?.disabled || disabled;
                        label = formDataState?.label || label;
                        tooltip = formDataState?.tooltip || tooltip;
                      });
                    }

                    switch (field.type) {
                      case 'input-text':
                        element = (
                          <Input
                            type="text"
                            name={fieldName}
                            label={label}
                            value={value}
                            onChange={handleInputChange}
                            autoComplete="off"
                            disabled={loading || disabled}
                            title={tooltip}
                            placeholder={placeholder}
                            validation={
                              formErrors[fieldName]
                                ? { valid: false, message: formErrors[fieldName] }
                                : undefined
                            }
                            isOptional={true}
                          />
                        );
                        break;
                      default:
                        break;
                    }

                    return (
                      <div key={key} className="form-block vertical">
                        {element}
                      </div>
                    );
                  }
                })}
              </div>
            )}

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddDatasourceForm = forwardRef(_AddDatasourceForm);
